function Contact({ user, onMessageSubmit }) {
    try {
        const [formData, setFormData] = React.useState({
            name: user ? user.name : '',
            email: user ? user.email : '',
            subject: '',
            message: ''
        });
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        const [submitStatus, setSubmitStatus] = React.useState(null);
        const [errorMessage, setErrorMessage] = React.useState('');

        const handleSubmit = async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            setSubmitStatus(null);
            setErrorMessage('');

            try {
                // Validate form data
                if (!formData.name || !formData.email || !formData.subject || !formData.message) {
                    throw new Error('Please fill in all required fields');
                }

                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    throw new Error('Please enter a valid email address');
                }

                const messageData = {
                    ...formData,
                    userId: user?.id,
                    date: new Date().toISOString(),
                    status: 'new'
                };

                // Ensure Firebase authentication is available
                if (!window.firebaseAuth) {
                    // Try to initialize anonymous auth if needed
                    try {
                        await window.firebaseAuth.signInAnonymously();
                        console.log('Signed in anonymously for message submission');
                    } catch (authError) {
                        console.warn('Anonymous auth failed for message submission:', authError);
                        // Continue anyway, we'll handle in the message service
                    }
                }

                // Check if message service is available
                if (!window.messageService) {
                    throw new Error('Message service is not available');
                }

                // Use message service to create message
                const messageId = await window.messageService.createMessage(messageData);
                
                if (messageId.startsWith('temp-')) {
                    // This is a temporary ID, indicating an error occurred but was handled
                    console.warn('Message created with temporary ID due to errors:', messageId);
                    setSubmitStatus('success'); // Still show success to user
                } else {
                    console.log('Message created successfully:', messageId);
                    setSubmitStatus('success');
                }
                
                setFormData({
                    name: user ? user.name : '',
                    email: user ? user.email : '',
                    subject: '',
                    message: ''
                });

                if (onMessageSubmit) {
                    onMessageSubmit(messageData);
                }
            } catch (error) {
                console.error('Message submission error:', error);
                setSubmitStatus('error');
                setErrorMessage(error.message || 'Failed to send your message. Please try again.');
                reportError(error);
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleChange = (e) => {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value
            });
        };

        return (
            <section data-name="contact" id="contact" className="section py-24 bg-gray-900 relative">
                {/* Background elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10">
                        <div className="absolute w-96 h-96 rounded-full bg-green-500/30 blur-3xl top-20 right-20"></div>
                        <div className="absolute w-64 h-64 rounded-full bg-blue-500/20 blur-3xl bottom-20 left-20"></div>
                    </div>
                </div>
                
                <div className="container relative z-10 max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        <div className="relative">
                            <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700 h-full">
                                <h2 className="text-4xl font-bold mb-6 text-white">
                                    Get <span className="text-green-400">In Touch</span>
                                </h2>
                                
                                <p className="text-gray-300 mb-8">
                                    Have questions about our mission or want to learn how you can contribute? Reach out to us and we'll get back to you as soon as possible.
                                </p>
                                
                                <div className="space-y-6 mb-8">
                                    <div className="flex items-start">
                                        <div className="bg-green-500/20 text-green-400 p-3 rounded-lg mr-4 flex-shrink-0">
                                            <i className="fas fa-envelope text-xl"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-white text-lg">Email</h3>
                                            <p className="text-gray-400">info@sustainablecomputenetwork.org</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start">
                                        <div className="bg-green-500/20 text-green-400 p-3 rounded-lg mr-4 flex-shrink-0">
                                            <i className="fas fa-map-marker-alt text-xl"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-white text-lg">Location</h3>
                                            <p className="text-gray-400">London, United Kingdom</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start">
                                        <div className="bg-green-500/20 text-green-400 p-3 rounded-lg mr-4 flex-shrink-0">
                                            <i className="fas fa-clock text-xl"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-white text-lg">Office Hours</h3>
                                            <p className="text-gray-400">Monday - Friday: 9am - 5pm GMT</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex space-x-4">
                                    <a href="#" className="bg-gray-700 hover:bg-gray-600 w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors">
                                        <i className="fab fa-twitter"></i>
                                    </a>
                                    <a href="#" className="bg-gray-700 hover:bg-gray-600 w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors">
                                        <i className="fab fa-linkedin-in"></i>
                                    </a>
                                    <a href="#" className="bg-gray-700 hover:bg-gray-600 w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors">
                                        <i className="fab fa-github"></i>
                                    </a>
                                </div>
                                
                                {/* Decorative elements */}
                                <div className="absolute -bottom-8 -left-8 w-40 h-40 border-4 border-green-500/20 rounded-xl -z-10"></div>
                                <div className="absolute -top-8 -right-8 w-24 h-24 bg-green-500/10 rounded-xl -z-10"></div>
                            </div>
                        </div>
                        
                        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
                            {submitStatus === 'success' ? (
                                <div className="text-center p-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
                                        <i className="fas fa-check text-green-400 text-4xl"></i>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2 text-white">Thank You!</h3>
                                    <p className="text-gray-300 mb-6">Your message has been sent successfully. We'll get back to you soon.</p>
                                    <button
                                        onClick={() => setSubmitStatus(null)}
                                        className="btn btn-primary"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-white mb-2 font-medium">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                disabled={user}
                                                className="w-full px-4 py-3 bg-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 border border-gray-600 text-white"
                                                placeholder="Enter your name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-white mb-2 font-medium">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={user}
                                                className="w-full px-4 py-3 bg-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 border border-gray-600 text-white"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-white mb-2 font-medium">
                                            Subject <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 border border-gray-600 text-white"
                                            placeholder="Enter message subject"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white mb-2 font-medium">
                                            Message <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="6"
                                            className="w-full px-4 py-3 bg-gray-700 rounded-lg focus:ring-2 focus:ring-green-400 border border-gray-600 text-white"
                                            placeholder="Enter your message"
                                        ></textarea>
                                    </div>

                                    {submitStatus === 'error' && (
                                        <div className="p-4 bg-red-500/10 text-red-400 rounded-lg">
                                            <i className="fas fa-exclamation-circle mr-2"></i>
                                            {errorMessage || 'There was an error submitting your message. Please try again.'}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="btn btn-primary w-full"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane mr-2"></i>
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('Contact error:', error);
        reportError(error);
        return (
            <section data-name="contact" id="contact" className="section bg-gray-50/50 backdrop-blur-sm">
                <div className="container max-w-4xl">
                    <h2 data-name="contact-title" className="text-5xl font-bold text-center mb-16 text-gray-800">
                        Get <span className="text-green-600">Involved</span>
                    </h2>
                    <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-xl p-8 text-center">
                        <div className="text-red-500 text-5xl mb-4">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Something Went Wrong</h3>
                        <p className="text-gray-600 mb-6">We're having trouble loading the contact form. Please try again later or contact us directly at info@sustainablecomputenetwork.org</p>
                    </div>
                </div>
            </section>
        );
    }
}

window.Contact = Contact;

export default Contact;
